import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Big from "big.js";
import { useStoreState } from "../../../store/useStore";
import useFetch from "../../../../hooks/useFetch";
import type {
  ApiResponse,
  RawTicker,
  RawSymbolInfo,
  MarketItem,
  QuoteCurrencyTab,
  MarketContext
} from "../types";

/** 轮询间隔（ms） */
const POLL_INTERVAL = 5000;

// ─── API 端点映射 ────────────────────────────────────────────

/** 币对信息 API（获取有效交易对列表 + 元数据） */
const SYMBOLS_ENDPOINTS: Record<MarketContext, string> = {
  spot: "sapi/v1/basic/symbols",
  futures: "uapi/v3/basic/symbols"
};

/** 行情 API（获取所有 ticker 数据） */
const TICKER_ENDPOINTS: Record<MarketContext, string> = {
  spot: "sapi/v1/market/symbol/all",
  futures: "uapi/v3/market/symbol/all"
};

// ─── 搜索过滤算法 ─────────────────────────────────────────────
// 与 blofin-component WatchList/SearchList 的 filterAndSortSymbolsBySearch 逻辑一致
// 优先级：base currency 精确匹配 > symbol 前缀匹配 > symbol 包含搜索词

function filterAndSortBySearch(items: MarketItem[], search: string): MarketItem[] {
  if (!search.trim()) return items;

  const searchUpper = search.toUpperCase();

  const filtered = items.filter((item) => {
    if (!item.symbol || !item.baseCurrency) return false;
    const symbolUpper = item.symbol.replace("-", "").toUpperCase();
    const baseCurrencyUpper = item.baseCurrency.toUpperCase();
    return baseCurrencyUpper === searchUpper || symbolUpper.includes(searchUpper);
  });

  return filtered.sort((a, b) => {
    const aSymbol = a.symbol.replace("-", "").toUpperCase();
    const bSymbol = b.symbol.replace("-", "").toUpperCase();
    const aBase = a.baseCurrency.toUpperCase();
    const bBase = b.baseCurrency.toUpperCase();

    // 1. base currency 精确匹配排最前
    if (aBase === searchUpper && bBase !== searchUpper) return -1;
    if (aBase !== searchUpper && bBase === searchUpper) return 1;

    // 2. symbol 前缀匹配排第二
    if (aSymbol.startsWith(searchUpper) && !bSymbol.startsWith(searchUpper)) return -1;
    if (!aSymbol.startsWith(searchUpper) && bSymbol.startsWith(searchUpper)) return 1;

    return 0;
  });
}

// ─── 24h 涨跌幅计算（与 WatchList change24 逻辑一致，使用 Big.js 精确运算） ────

function calcChange24(ticker: RawTicker): number {
  const close = Number(ticker.close);
  const open = Number(ticker.open);
  if (!Number.isFinite(close) || !Number.isFinite(open) || open === 0) return 0;
  try {
    const result = Number(new Big(close).minus(open).div(open).times(100));
    return Number.isFinite(result) ? result : 0;
  } catch {
    return 0;
  }
}

// ─── Hook ─────────────────────────────────────────────────────

interface UseSearchMarketReturn {
  /** 当前激活 tab（按 quote currency 过滤）— 仅 Spot 有 tab 模式时有意义 */
  activeTab: QuoteCurrencyTab;
  /** 切换 tab */
  setActiveTab: (tab: QuoteCurrencyTab) => void;
  /** 搜索关键词 */
  search: string;
  /** 更新搜索关键词 */
  setSearch: (val: string) => void;
  /** 过滤后的结果列表 */
  results: MarketItem[];
  /** 是否正在加载（首次） */
  loading: boolean;
  /** 是否处于搜索模式 */
  isSearching: boolean;
  /** 默认展示列表（无搜索时按成交额 top） */
  topList: MarketItem[];
}

/**
 * useSearchMarket — MegaMenu 搜索行情 hook
 *
 * 完全对齐 blofin-component WatchList 的数据逻辑：
 * 1. 先加载币对列表（/basic/symbols）获取有效交易对 + 元数据（logo、quote_currency 等）
 * 2. 再加载行情数据（/market/symbol/all），用币对列表过滤 & 合并
 * 3. 5 秒轮询刷新行情
 *
 * @param visible 面板是否可见（控制轮询启停）
 * @param context 市场上下文：spot 或 futures
 * @param externalQuoteCurrency 外部指定 quote currency（传入后跳过内部 Tab，如 "USDT"、"USDC"、"USD"）
 */
const useSearchMarket = (
  visible: boolean,
  context: MarketContext = "spot",
  externalQuoteCurrency?: string
): UseSearchMarketReturn => {
  const { baseUrl } = useStoreState();
  const { get } = useFetch(baseUrl);

  const [activeTab, setActiveTab] = useState<QuoteCurrencyTab>("USDT");
  const [search, setSearch] = useState("");

  const [dataList, setDataList] = useState<MarketItem[]>([]);
  const [loading, setLoading] = useState(true);

  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  // 用 ref 保存 get 函数，保证 callback 引用稳定（useFetch 每次渲染返回新引用）
  const fetchRef = useRef(get);
  fetchRef.current = get;

  // 币对信息缓存（symbol → RawSymbolInfo），只需加载一次
  const symbolsMapRef = useRef<Record<string, RawSymbolInfo>>({});

  // 实际生效的筛选值：外部指定 > 内部 Tab
  const effectiveFilter = externalQuoteCurrency || activeTab;

  // ─── 1. 加载币对列表 ──────────────────────────────────────

  const loadSymbols = useCallback(async () => {
    // 已缓存则跳过
    if (Object.keys(symbolsMapRef.current).length > 0) return true;

    try {
      const endpoint = SYMBOLS_ENDPOINTS[context];
      const res = await fetchRef.current<ApiResponse<RawSymbolInfo[]>>(endpoint);
      if (res && res.code === 200 && Array.isArray(res.data)) {
        const map: Record<string, RawSymbolInfo> = {};
        for (const item of res.data) {
          map[item.symbol] = item;
        }
        symbolsMapRef.current = map;
        return true;
      }
    } catch {
      // baseUrl 为空或请求失败时 useFetch 会 reject
    }
    return false;
  }, [context]);

  // ─── 2. 加载行情并与币对信息合并（与 WatchList getSpots 逻辑一致）

  const fetchTickers = useCallback(async () => {
    const symbolsMap = symbolsMapRef.current;
    if (Object.keys(symbolsMap).length === 0) return;

    try {
      const endpoint = TICKER_ENDPOINTS[context];
      const res = await fetchRef.current<ApiResponse<RawTicker[]>>(endpoint);
      if (res && res.code === 200 && Array.isArray(res.data)) {
        const items: MarketItem[] = res.data
          // 只保留在币对列表中的有效交易对（与 WatchList filter 逻辑一致）
          .filter((ticker) => symbolsMap[ticker.symbol])
          .map((ticker) => {
            const info = symbolsMap[ticker.symbol];
            // 直接使用 currency_type 作为 contractType（与 WatchList mapSymbols 逻辑一致）
            // currency_type 值: "usdt" (USDT-M) | "usd" (Coin-M) | "usdc" (USDC-M)
            const currencyType: string | undefined = info.currency_type || undefined;

            // Futures volume: Coin-M (currency_type === "usd") 用 quantity，其余用 amount
            // 与 WatchList: transformed.currency_type !== FuturesStrategyEnum.CMP 逻辑一致
            const volume =
              currencyType === "usd" ? Number(ticker.quantity) || 0 : Number(ticker.amount) || 0;

            return {
              symbol: ticker.symbol,
              baseCurrency: info.base_currency,
              quoteCurrency: info.quote_currency,
              price: Number(ticker.close),
              change24: calcChange24(ticker),
              volume,
              logo: info.logo || "",
              sort: info.sort ?? 9999,
              spotType: info.spot_type || undefined,
              contractType: currencyType
            };
          });
        setDataList(items);
      }
    } catch {
      // baseUrl 为空或请求失败时静默处理
    }
  }, [context]);

  // ─── 轮询控制 ──────────────────────────────────────────────

  useEffect(() => {
    if (!visible) return;

    let cancelled = false;

    const init = async () => {
      // 第一步：加载币对列表
      const ok = await loadSymbols();
      if (cancelled) return;

      if (!ok) {
        setLoading(false);
        return;
      }

      // 第二步：加载行情数据
      await fetchTickers();
      if (!cancelled) setLoading(false);

      // 启动轮询（只轮询 ticker，symbols 不需要反复加载）
      pollTimer.current = setInterval(fetchTickers, POLL_INTERVAL);
    };

    setLoading(true);
    init();

    return () => {
      cancelled = true;
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, [visible, loadSymbols, fetchTickers]);

  // context 变化时清空缓存（spot → futures 或反过来）
  useEffect(() => {
    symbolsMapRef.current = {};
  }, [context]);

  // ─── 筛选逻辑（与 WatchList 一致） ────────────────────────
  // Spot：按 spot_type 筛选（"USDT" / "USDC"）
  // Futures：按 formatted_contract_type 筛选（"usdt" / "usdc" / "usd"）

  const filteredList = useMemo(() => {
    if (context === "spot") {
      // Spot 用 spot_type 字段筛选（与 WatchList item.spot_type === spotType 一致）
      return dataList.filter(
        (item) => item.spotType?.toUpperCase() === effectiveFilter.toUpperCase()
      );
    } else {
      // Futures 用 contractType 字段筛选（与 WatchList item.formatted_contract_type === futuresType 一致）
      // effectiveFilter 来自 hoveredItem: "USDT" → "usdt", "USDC" → "usdc", "USD" → "usd"
      return dataList.filter((item) => item.contractType === effectiveFilter.toLowerCase());
    }
  }, [dataList, effectiveFilter, context]);

  // ─── 搜索结果 ─────────────────────────────────────────────

  const isSearching = search.trim().length > 0;

  const results = useMemo(() => {
    if (!isSearching) return [];
    return filterAndSortBySearch(filteredList, search);
  }, [filteredList, search, isSearching]);

  // 默认列表：按 sort 字段降序（值越大越靠前，与 WatchList 默认顺序一致）
  const topList = useMemo(() => {
    return [...filteredList].sort((a, b) => b.sort - a.sort);
  }, [filteredList]);

  return {
    activeTab,
    setActiveTab,
    search,
    setSearch,
    results,
    loading,
    isSearching,
    topList
  };
};

export default useSearchMarket;
